import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import {
    addCartItem,
    clearActiveCart,
    clearCartCache,
    createOrderFromCart,
    decreaseCartItem,
    removeCartItem,
    removeCartItemFromCache,
    syncCartQueryData,
} from './api';
import type { CartItem } from './types';

import { extractApiError } from '@/lib/shared-utils';
import type { StockError } from '@/pages/MyOrders/types';

export function useCartMutations(supplierId: number | undefined) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [pendingProductSlug, setPendingProductSlug] = useState<string | null>(null);
    const [editingQuantities, setEditingQuantities] = useState<Record<string, number>>({});
    const [stockErrors, setStockErrors] = useState<StockError[]>([]);

    const invalidateCart = () => queryClient.invalidateQueries({
        queryKey: ['cart'],
    });

    const requireSupplierId = () => {
        if (supplierId === undefined) {
            throw new Error('Cart supplier is unavailable.');
        }
        return supplierId;
    };

    const runCartAction = async <T, >(
        productSlug: string | null,
        action: () => Promise<T>,
        successMessage?: string,
    ) => {
        try {
            setPendingProductSlug(productSlug);
            await action();
            await invalidateCart();
            // The quantities just changed, so any stock error shown against
            // the previous checkout attempt no longer reflects the cart.
            setStockErrors([]);
            if (successMessage) toast.success(successMessage);
        } catch (mutationError) {
            toast.error(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Cart update failed.',
            );
        } finally {
            setPendingProductSlug(null);
        }
    };

    const clearCartMutation = useMutation({
        mutationFn: clearActiveCart,
        onSuccess: async (response) => {
            queryClient.setQueryData(
                ['cart'],
                (current: unknown) => (
                    response.ok
                        ? syncCartQueryData(
                            current,
                            response.data,
                            response.status,
                            response.statusText,
                        )
                        : clearCartCache(current)
                ),
            );
            await invalidateCart();
            toast.success('Cart cleared successfully.');
        },
        onError: (mutationError) => {
            toast.error(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Unable to clear the cart.',
            );
        },
    });

    const checkoutMutation = useMutation({
        mutationFn: () => {
            setStockErrors([]);
            return createOrderFromCart();
        },
        onSuccess: (response) => {
            if (!response.ok) {
                const stock = response.data?.stock;
                if (Array.isArray(stock) && stock.length > 0) {
                    setStockErrors(stock as StockError[]);
                    toast.error('Some items exceed the available stock. See below.');
                    return;
                }
                toast.error(extractApiError(response.data, 'Unable to create the order.'));
                return;
            }
            toast.success('Order created successfully.');
            // Navigate away first so the cart page doesn't re-render with its
            // now-empty state before the route change takes effect.
            if (response.data?.uuid) {
                navigate(`/orders/${response.data.uuid}`);
            }
            invalidateCart();
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
        onError: (mutationError) => {
            toast.error(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Unable to create the order.',
            );
        },
    });

    const increaseQuantity = async (item: CartItem) => {
        await runCartAction(item.product_slug, async () => {
            const response = await addCartItem({
                product_slug: item.product_slug,
                unit_slug: item.unit_slug,
                quantity: 1,
                supplier_id: requireSupplierId(),
            });
            if (!response.ok) {
                throw new Error(
                    extractApiError(response.data, 'Unable to increase quantity.'),
                );
            }
        });
    };

    const decreaseQuantity = async (item: CartItem) => {
        await runCartAction(item.product_slug, async () => {
            const response = await decreaseCartItem({
                product_slug: item.product_slug,
                quantity: 1,
            });
            if (!response.ok) {
                throw new Error(
                    extractApiError(response.data, 'Unable to decrease quantity.'),
                );
            }
        });
    };

    const commitQuantity = async (item: CartItem) => {
        const desired = editingQuantities[item.product_slug];
        if (desired === undefined || desired === item.quantity) {
            setEditingQuantities((prev) => {
                const next = { ...prev };
                delete next[item.product_slug];
                return next;
            });
            return;
        }
        const diff = desired - item.quantity;
        if (diff > 0) {
            await runCartAction(item.product_slug, async () => {
                const response = await addCartItem({
                    product_slug: item.product_slug,
                    unit_slug: item.unit_slug,
                    quantity: diff,
                    supplier_id: requireSupplierId(),
                });
                if (!response.ok) {
                    throw new Error(extractApiError(response.data, 'Unable to update quantity.'));
                }
            });
        } else {
            await runCartAction(item.product_slug, async () => {
                const response = await decreaseCartItem({
                    product_slug: item.product_slug,
                    quantity: Math.abs(diff),
                });
                if (!response.ok) {
                    throw new Error(extractApiError(response.data, 'Unable to update quantity.'));
                }
            });
        }
        setEditingQuantities((prev) => {
            const next = { ...prev };
            delete next[item.product_slug];
            return next;
        });
    };

    const changeUnit = async (item: CartItem, nextUnitSlug: string) => {
        if (nextUnitSlug === item.unit_slug) return;
        await runCartAction(
            item.product_slug,
            async () => {
                const removeRes = await removeCartItem(item.product_slug);
                if (!removeRes.ok) {
                    throw new Error(
                        extractApiError(removeRes.data, 'Unable to change unit.'),
                    );
                }
                const addRes = await addCartItem({
                    product_slug: item.product_slug,
                    unit_slug: nextUnitSlug,
                    quantity: item.quantity,
                    supplier_id: requireSupplierId(),
                });
                if (!addRes.ok) {
                    throw new Error(
                        extractApiError(addRes.data, 'Unable to change unit.'),
                    );
                }
            },
            'Unit updated.',
        );
    };

    const removeItem = async (item: CartItem) => {
        await runCartAction(
            item.product_slug,
            async () => {
                const response = await removeCartItem(item.product_slug);
                if (!response.ok) {
                    throw new Error(
                        extractApiError(response.data, 'Unable to remove item from cart.'),
                    );
                }
                queryClient.setQueryData(
                    ['cart'],
                    (current: unknown) => (
                        response.ok
                            ? syncCartQueryData(
                                current,
                                response.data,
                                response.status,
                                response.statusText,
                            )
                            : removeCartItemFromCache(current, item.product_slug)
                    ),
                );
            },
            'Item removed from cart.',
        );
    };

    const handleEditQuantity = (slug: string, value: number) => {
        setEditingQuantities((prev) => ({ ...prev, [slug]: value }));
    };

    return {
        pendingProductSlug,
        stockErrors,
        editingQuantities,
        handleEditQuantity,
        clearCartMutation,
        checkoutMutation,
        increaseQuantity,
        decreaseQuantity,
        commitQuantity,
        changeUnit,
        removeItem,
    };
}
